import { cva } from "class-variance-authority";
import {
  AlertCircle,
  Info,
  LoaderCircle,
  TriangleAlert,
  WifiOff,
} from "lucide-react";
import { useId, type HTMLAttributes, type ReactNode } from "react";

import { cn } from "./cn.js";

export function Panel({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return (
    <section
      className={cn("rounded-md border border-border bg-surface", className)}
      {...props}
    />
  );
}

export type StateKind = "empty" | "loading" | "error" | "offline";

const stateSurfaceVariants = cva(
  "grid min-h-44 place-items-center rounded-md border px-6 py-10 text-center",
  {
    variants: {
      kind: {
        empty: "border-dashed border-border-hi bg-bg-2",
        loading: "border-dashed border-border-hi bg-bg-2",
        error:
          "border-[color-mix(in_oklch,var(--err)_40%,var(--border))] bg-[color-mix(in_oklch,var(--err)_8%,var(--bg-2))]",
        offline: "border-border-hi bg-surface",
      },
    },
    defaultVariants: { kind: "empty" },
  },
);

const stateIcons = {
  empty: Info,
  loading: LoaderCircle,
  error: AlertCircle,
  offline: WifiOff,
} satisfies Record<StateKind, typeof Info>;

const stateIconClasses: Record<StateKind, string> = {
  empty: "text-ink-3",
  loading: "animate-[radio-spin_1s_linear_infinite] text-ink-3",
  error: "text-err",
  offline: "text-ink-2",
};

export function StateSurface({
  action,
  children,
  className,
  kind = "empty",
  title,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  action?: ReactNode;
  kind?: StateKind;
  title: ReactNode;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const Icon = stateIcons[kind];
  return (
    <div
      role={kind === "error" ? "alert" : "status"}
      aria-atomic="true"
      aria-busy={kind === "loading" || undefined}
      aria-describedby={children ? descriptionId : undefined}
      aria-labelledby={titleId}
      aria-live={kind === "error" ? "assertive" : "polite"}
      className={cn(stateSurfaceVariants({ kind }), className)}
      {...props}
    >
      <div className="grid max-w-md justify-items-center gap-3">
        <Icon
          aria-hidden="true"
          className={cn("size-5", stateIconClasses[kind])}
        />
        <div>
          <div id={titleId} className="font-semibold text-ink">
            {title}
          </div>
          {children ? (
            <div
              id={descriptionId}
              className="mt-1 text-sm leading-6 text-ink-3"
            >
              {children}
            </div>
          ) : null}
        </div>
        {action ? <div className="mt-1">{action}</div> : null}
      </div>
    </div>
  );
}

const noticeVariants = cva(
  "flex items-start gap-3 rounded-md border px-4 py-3 text-sm",
  {
    variants: {
      tone: {
        neutral: "border-border-hi bg-surface text-ink-2",
        warning:
          "border-[color-mix(in_oklch,var(--warn)_40%,var(--border))] bg-[color-mix(in_oklch,var(--warn)_10%,transparent)] text-ink",
        danger:
          "border-[color-mix(in_oklch,var(--err)_40%,var(--border))] bg-[color-mix(in_oklch,var(--err)_10%,transparent)] text-ink",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

const noticeIcons = {
  neutral: Info,
  warning: TriangleAlert,
  danger: AlertCircle,
};

export type NoticeTone = keyof typeof noticeIcons;

const noticeIconClasses: Record<NoticeTone, string> = {
  neutral: "text-ink-3",
  warning: "text-warn",
  danger: "text-err",
};

export function Notice({
  children,
  className,
  title,
  tone = "neutral",
  ...props
}: Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  title?: ReactNode;
  tone?: NoticeTone;
}) {
  const Icon = noticeIcons[tone];
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={cn(noticeVariants({ tone }), className)}
      {...props}
    >
      <Icon
        aria-hidden="true"
        className={cn("mt-0.5 size-4 shrink-0", noticeIconClasses[tone])}
      />
      <div className="min-w-0 flex-1">
        {title ? <div className="font-semibold text-ink">{title}</div> : null}
        {children ? (
          <div className={cn("leading-5", title && "mt-1 text-ink-2")}>
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
}
